import i18n from '@/locales/i18n';
import { ref } from 'vue';
import { message } from '@koi-design/uxd-ui';
import {
  getMultiAgentInfoApi,
  getOpenApiMultiAgentDetailsInfoApi,
  getSegmentCode,
  createSegmentCode,
  getMultiAgentConversationListApi,
  getMultiAgentDetailsInfoApi
} from '@/api/multiAgent';
import {
  mergeLongTermMemoryFromConfig,
  stripLegacyLongTermKeysFromAgentConfigRoot
} from '@/views/multiAgent/longTermMemoryMerge';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { createChatSessionConfigSnapshot } from '@/views/multiAgent/chatSessionConfig';

// Shared state - all components using this hook will share the same data
const multiAgentInfo = ref({});
const loading = ref(false);
const appendParams = ref({});
const flowDisabled = ref(false);
const curComponent = ref({
  type: '',
  id: ''
});
const drawVisible = ref(false);
const chatDrawerVisible = ref(false);
const showChatDrawerType = ref('');
const multiAgentConversationFlag = ref(false);
const chatSessionConfigSnapshot = ref(createChatSessionConfigSnapshot());
// Conversation list state
const conversationList = ref([]);
const conversationLoading = ref(false);
const conversationHasMore = ref(true);
const lastConversationId = ref('');
export function useMultiAgentInfo() {
  const store = useStore();
  const route = useRoute();

  // Set curComponent and update drawVisible accordingly
  const setCurComponent = ({ type, id }) => {
    if (id) {
      drawVisible.value = true;
    } else {
      drawVisible.value = false;
    }
    curComponent.value = {
      type,
      id
    };
  };

  // Set drawVisible directly
  const setDrawVisible = visible => {
    drawVisible.value = visible;
  };
  // Set multiAgentConversationFlag
  const setMultiAgentConversationFlag = flag => {
    multiAgentConversationFlag.value = flag;
  };
  const snapshotChatSessionConfig = (segmentCode, welcome) => {
    const config = multiAgentInfo.value.multi_agent_config || {};
    chatSessionConfigSnapshot.value = createChatSessionConfigSnapshot(
      segmentCode,
      config,
      welcome
    );
  };
  // Set chatDrawerVisible and optionally set showChatDrawerType
  const setChatDrawerVisible = (visible, type = '') => {
    chatDrawerVisible.value = visible;
    if (visible && type) {
      showChatDrawerType.value = type;
    } else if (!visible) {
      showChatDrawerType.value = '';
      setMultiAgentConversationFlag(false);
    }
  };
  const formatMultiAgentInfo = data => {
    if (!data) return data;
    const formatted = {
      ...data
    };

    // Store append params
    appendParams.value = {
      homeland: data.homeland_id || store.getters.homelandInfo?.homeland,
      creator: data.creator
    };
    return formatted;
  };
  const getMultiAgentInfo = async () => {
    const { homelandInfo } = store.getters;
    const data = await getMultiAgentInfoApi({
      id: route.params.id,
      homeland_id: homelandInfo.homeland
    });
    const formattedData = formatMultiAgentInfo(data);
    return formattedData;
  };
  const generateSegmentCode = async params => {
    let paramsRes = {};
    if (!params) {
      if (multiAgentInfo.value.message_source === 'web_share') {
        paramsRes = {
          cybertron_robot_key: route.query.key,
          cybertron_robot_token: route.query.token,
          message_source: multiAgentInfo.value.message_source
        };
      } else {
        paramsRes = {
          multi_agent_id: Number(route.params.id),
          message_source: multiAgentInfo.value.message_source
        };
      }
    } else {
      paramsRes = params;
    }
    const res = await createSegmentCode(paramsRes);
    if (res) {
      multiAgentInfo.value.segment_code = res.segment_code;
      snapshotChatSessionConfig(res.segment_code, res.welcome);
    }
  };
  const getLastSegmentCode = async () => {
    let params = {};
    if (multiAgentInfo.value.message_source === 'web_share') {
      params = {
        cybertron_robot_key: route.query.key,
        cybertron_robot_token: route.query.token,
        message_source: multiAgentInfo.value.message_source
      };
    } else {
      params = {
        multi_agent_id: Number(route.params.id),
        message_source: multiAgentInfo.value.message_source
      };
    }
    try {
      const res = await getSegmentCode(params);
      if (res) {
        multiAgentInfo.value.segment_code = res.segment_code;
        if (chatSessionConfigSnapshot.value.segmentCode !== res.segment_code) {
          snapshotChatSessionConfig(res.segment_code);
        }
      } else {
        generateSegmentCode(params);
      }
    } catch {
      generateSegmentCode(params);
    }
  };
  const initMultiAgentInfo = async (messageSource = 'web_preview') => {
    loading.value = true;
    try {
      let data = {};
      if (messageSource === 'web_share') {
        data = await getOpenApiMultiAgentDetailsInfoApi({
          cybertron_robot_id: route.query.id,
          cybertron_robot_key: route.query.key,
          cybertron_robot_token: route.query.token,
          message_source: messageSource
        });
      } else if (messageSource === 'web_preview') {
        data = await getMultiAgentInfo();
      } else {
        data = await getMultiAgentDetailsInfoApi({
          multi_agent_id: route.params.id
        });
      }
      // Assign all data after API returns
      multiAgentInfo.value = {
        ...data,
        showMiniMap: false,
        message_source: messageSource
      };
      if (!multiAgentInfo.value.multi_agent_config) {
        multiAgentInfo.value.multi_agent_config = {};
      }
      const normalizedLt = mergeLongTermMemoryFromConfig(
        multiAgentInfo.value.multi_agent_config
      );
      multiAgentInfo.value.multi_agent_config.long_term_memory_config = normalizedLt;
      stripLegacyLongTermKeysFromAgentConfigRoot(
        multiAgentInfo.value.multi_agent_config
      );
      multiAgentInfo.value.long_term_memory_config = normalizedLt;
      getLastSegmentCode();
      return data;
    } catch (error) {
      message.error(error);
      throw error;
    } finally {
      loading.value = false;
    }
  };

  // Reset conversation list
  const resetConversationList = () => {
    conversationList.value = [];
    lastConversationId.value = '';
    conversationHasMore.value = true;
  };

  // Refresh conversation list
  const refreshConversationList = async ({ multiAgentId, homelandId }) => {
    if (!multiAgentId) {
      console.warn('Multi agent ID are required');
      return;
    }
    try {
      conversationLoading.value = true;
      const res = await getMultiAgentConversationListApi({
        multi_agent_id: multiAgentId,
        homeland_id: homelandId,
        message_source: multiAgentInfo.value.message_source,
        cybertron_robot_token:
          multiAgentInfo.value.message_source === 'web_share'
            ? route.query.token
            : undefined,
        cybertron_robot_key:
          multiAgentInfo.value.message_source === 'web_share'
            ? route.query.key
            : undefined
      });
      if (Array.isArray(res) && res.length > 0) {
        conversationList.value = res;
      } else {
        conversationList.value = [];
      }
    } catch (error) {
      console.error('Failed to fetch conversation list:', error);
      message.error(
        i18n.global.t(
          'views.multiAgent.useData.7b039aced051ad67e9bb8a65899ec1a2'
        )
      );
      conversationList.value = [];
    } finally {
      conversationLoading.value = false;
    }
  };

  return {
    // Shared reactive state - all components share the same refs
    multiAgentInfo,
    loading,
    appendParams,
    curComponent,
    drawVisible,
    chatDrawerVisible,
    showChatDrawerType,
    multiAgentConversationFlag,
    chatSessionConfigSnapshot,
    flowDisabled,
    // Conversation list state
    conversationList,
    conversationLoading,
    conversationHasMore,
    lastConversationId,
    setCurComponent,
    setDrawVisible,
    setChatDrawerVisible,
    setMultiAgentConversationFlag,
    snapshotChatSessionConfig,
    formatMultiAgentInfo,
    initMultiAgentInfo,
    getLastSegmentCode,
    generateSegmentCode,
    refreshConversationList,
    resetConversationList
  };
}
